import React from 'react';
import { useIntl, defineMessages, FormattedNumber } from 'react-intl';
import {
	differenceInMinutes,
	differenceInHours,
	differenceInDays,
	differenceInWeeks,
} from 'date-fns';

const messages = defineMessages({
	now: {
		id: 'inline-comments-utils.relative-timestamp.now',
		defaultMessage: 'Now',
		description: 'Timestamp showing "Now" for very recent comments (< 2 minutes)',
	},
});

interface RelativeTimestampProps {
	date: Date;
}

export const RelativeTimestamp: React.FC<RelativeTimestampProps> = ({ date }) => {
	const intl = useIntl();
	const now = new Date();
	const diffInMinutes = differenceInMinutes(now, date);
	const diffInHours = differenceInHours(now, date);
	const diffInDays = differenceInDays(now, date);
	const diffInWeeks = differenceInWeeks(now, date);

	// < 2 minutes: "Now"
	if (diffInMinutes < 2) {
		return <>{intl.formatMessage(messages.now)}</>;
	}

	// < 1 hour: Shows minutes (e.g., "15m")
	if (diffInMinutes < 60) {
		return (
			// eslint-disable-next-line react/style-prop-object
			<FormattedNumber value={diffInMinutes} style="unit" unit="minute" unitDisplay="narrow" />
		);
	}

	// < 1 day: Shows hours (e.g., "3h")
	if (diffInHours < 24) {
		// eslint-disable-next-line react/style-prop-object
		return <FormattedNumber value={diffInHours} style="unit" unit="hour" unitDisplay="narrow" />;
	}

	// ≤ 1 week: Shows days (e.g., "5d")
	if (diffInWeeks < 1) {
		// eslint-disable-next-line react/style-prop-object
		return <FormattedNumber value={diffInDays} style="unit" unit="day" unitDisplay="narrow" />;
	}

	// > 1 week: Shows actual date
	const currentYear = now.getFullYear();
	const dateYear = date.getFullYear();

	// Same year: "Jan 15"
	if (currentYear === dateYear) {
		return (
			<>
				{intl.formatDate(date, {
					month: 'short',
					day: 'numeric',
				})}
			</>
		);
	}

	// Different year: "Jan 15, 2023"
	return (
		<>
			{intl.formatDate(date, {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			})}
		</>
	);
};
